import React, { useMemo } from 'react';
import { getIn } from 'formik';
import styles from './VariablesPanel.module.scss';
import Select from 'component/atom/forms/select/select/Select';
import Input from 'component/atom/forms/input/input/Input';
import TextArea from 'component/atom/forms/text-area/TextArea';
import Toggle from 'component/atom/forms/toggle/Toggle';
import Button from 'component/atom/forms/button/Button';

const VariablesPanel = ({ open, onClose, index, formik, typeOptions }) => {
  const fieldBase = `suggestedEntityQuestions.${index}`;

  const item = useMemo(() => {
    return formik.values?.suggestedEntityQuestions?.[index] ?? {};
  }, [formik.values, index]);

  const lookups = item.lookups ?? [];

  const setItemField = (path, value) => {
    formik.setFieldValue(`${fieldBase}.${path}`, value);
  };

  const handleVariableChange = (e) => {
    const raw = e?.target?.value ?? e?.value ?? e;

    if (raw === '' || raw === null || raw === undefined) {
      setItemField('variableId', '');
      return;
    }

    const newVariableId = Number(raw);
    if (!Number.isInteger(newVariableId)) return;

    const exists = (formik.values?.suggestedEntityQuestions ?? []).some(
      (q, i) => Number(q?.variableId) === newVariableId && i !== index,
    );

    if (exists) return;

    setItemField('variableId', newVariableId);
  };

  const handleLookupToggleChange = (e) => {
    const next =
      e?.target?.checked ??
      (typeof e?.target?.value === 'string'
        ? e.target.value === 'true'
        : e?.target?.value) ??
      Boolean(e);

    const enabled = Boolean(next);

    setItemField('isLookupEnabled', enabled);

    if (enabled && (lookups?.length ?? 0) === 0) {
      setItemField('lookups', [
        {
          id: null,
          captureNodeEntityQuestionId: item?.id || null,
          key: '',
          value: '',
        },
      ]);
    }
  };

  const handleAddEntry = (e) => {
    e?.preventDefault?.();
    setItemField('lookups', [
      ...lookups,
      {
        id: null,
        captureNodeEntityQuestionId: item?.id || null,
        key: '',
        value: '',
      },
    ]);
  };

  const handleRemoveEntry = (rowIndex) => (e) => {
    e?.preventDefault?.();
    const next = lookups.filter((_, i) => i !== rowIndex);
    setItemField('lookups', next);
  };

  const err = (path) => getIn(formik.errors, `${fieldBase}.${path}`);
  const tch = (path) => getIn(formik.touched, `${fieldBase}.${path}`);
  if (!open) return null;

  return (
    <div className={styles['notifications-panel']}>
      <div className={styles['cont']}>
        <Select
          label='Variable'
          name={`${fieldBase}.variableId`}
          options={typeOptions}
          value={item.variableId ?? ''}
          onChange={handleVariableChange}
        />
        <TextArea
          label='Question'
          placeholder='What would you like to ask?'
          name={`${fieldBase}.question`}
          value={item.question ?? ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          wrapperClassName={styles['question-text-area']}
          error={err('question')}
          touched={tch('question')}
        />
        <Input
          label='Validation Error Message'
          name={`${fieldBase}.validation_ErrorMessage`}
          value={item.validation_ErrorMessage ?? ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={err('validation_ErrorMessage')}
          touched={tch('validation_ErrorMessage')}
        />{' '}
        <Input
          label='Validation Rule'
          name={`${fieldBase}.validationRule`}
          value={item.validationRule ?? ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={err('validationRule')}
          touched={tch('validationRule')}
        />
        <div className={styles['toggle-container']}>
          <div className={styles.label}>
            <h6>Enable Lookup Table</h6>
            <p className={styles['toggle-desc']}>
              <span
                key={item.isLookupEnabled ? 'on' : 'off'}
                className={styles['toggle-desc-text']}
              >
                Map user responses to specific values
              </span>
            </p>
          </div>

          <Toggle
            name={`${fieldBase}.isLookupEnabled`}
            value={Boolean(item.isLookupEnabled)}
            onChange={handleLookupToggleChange}
            onBlur={formik.handleBlur}
            error={err('isLookupEnabled')}
            touched={tch('isLookupEnabled')}
          />
        </div>
        {Boolean(item.isLookupEnabled) && (
          <>
            <div className={styles['lookUp-table']}>
              <div className={styles['lookUp-header']}>
                <p className={styles.title}>Lookup Table</p>

                <div
                  type='button'
                  className={styles['lookUp-entryBtn']}
                  onClick={handleAddEntry}
                >
                  <Button
                    variant='primary-rev'
                    type='plus'
                    className={styles.button}
                  />
                  <p>Add Entry</p>
                </div>
              </div>

              <div className={styles['lookUp-container']}>
                {lookups.length === 0 ? (
                  <div className={styles['lookUp-empty']}>
                    <p>No entries yet. Add key-value pairs to map responses.</p>
                  </div>
                ) : (
                  lookups.map((row, rowIndex) => (
                    <div
                      className={styles['lookUp-fields']}
                      key={`lookup-${rowIndex}`}
                    >
                      <Input
                        label={0 === rowIndex ? 'Key (User Input)' : ''}
                        name={`${fieldBase}.lookups.${rowIndex}.key`}
                        value={row.key ?? ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={err(`lookups.${rowIndex}.key`)}
                        touched={tch(`lookups.${rowIndex}.key`)}
                        placeholder='E.g., Sick Leave'
                      />

                      <Input
                        label={0 === rowIndex ? 'Value' : ''}
                        name={`${fieldBase}.lookups.${rowIndex}.value`}
                        value={row.value ?? ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={err(`lookups.${rowIndex}.value`)}
                        touched={tch(`lookups.${rowIndex}.value`)}
                        placeholder='E.g., 1 or text'
                      />

                      <Button
                        type='delete'
                        variant='danger'
                        className={styles['delete-btn']}
                        onClick={handleRemoveEntry(rowIndex)}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={styles['footer']}>
              <Button label='Cancel' variant='light' onClick={onClose} />
              <Button type='save' label='Save' onClick={onClose} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VariablesPanel;
